import { Shapes, Smile, Sticker } from "lucide-react";
import { useState } from "react";
import {
  armedEmojiKey,
  emojiPlacementItem,
  widgetPlacementItem,
  type PlacementItem,
} from "../../../../hooks/placementItems";
import { WIDGET_PRESETS, type WidgetPreset } from "../../../../nodes/widgets";
import {
  EMOJI_SECTIONS,
  ICON_STICKER_PRESETS,
  REVIEW_STICKER_PRESETS,
  type EmojiEntry,
} from "../../../../nodes/widgets/ds";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_TILE_GLYPH,
} from "../../canvasToolbarComposition";
import {
  EmojiGrid,
  PlateGrid,
  StickerGrid,
  TabbedTray,
  TileGrid,
  TrayEmpty,
  TraySearch,
  TraySection,
  type TrayTab,
} from "../CanvasTrayContent";
import { insertEmojiIntoFocusedEditor, keepEditorFocus } from "../emojiInsert";
import { matchesWidgetQuery } from "../widgetSearch";
import { WidgetPreview, startWidgetDrag } from "./widgetPlates";

const PRODUCT_FAMILIES: {
  key: NonNullable<WidgetPreset["family"]>;
  name: string;
}[] = [
  { key: "status", name: "Status" },
  { key: "jargon", name: "Jargon" },
  { key: "callout", name: "Callouts" },
];

const tabGlyph = (Icon: typeof Smile) => (
  <Icon
    size={CANVAS_TILE_GLYPH}
    strokeWidth={CANVAS_GLYPH_STROKE}
    aria-hidden="true"
  />
);

/**
 * Stickers — the sheet's inventories behind one strip (ROUND3 §3, D39):
 * Emoji · Stickers · Icons, icon-only tabs in the sheet's order, search
 * first in the deck and spanning the open tab. Every tile arms the ghost
 * flow (ROUND2 §3): an emoji places a text label holding the character —
 * or, with a label or note editor focused, inserts it at the caret — the
 * drawn stickers and the icons place widgets. The Stickers tab leads with
 * the sheet's Review set, then the product's own Status · Jargon ·
 * Callouts, all searchable. Emotes and Games are absent, not disabled.
 */
export function StickersTray({
  value = null,
  onSelect,
}: {
  /** The armed item's id, or null. */
  value?: string | null;
  onSelect: (item: PlacementItem) => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const hit = (name: string) => name.toLowerCase().includes(q);

  // The grid reads bare keys; the armed id carries the emoji prefix.
  const armedEmoji = armedEmojiKey(value);

  const pickEmoji = (entry: EmojiEntry) => {
    if (insertEmojiIntoFocusedEditor(entry.char)) return;
    onSelect(emojiPlacementItem(entry));
  };

  const emojiBody = q
    ? (() => {
        const rows = EMOJI_SECTIONS.flatMap((s) => s.items).filter((it) =>
          hit(it.name),
        );
        return rows.length ? (
          <TraySection ariaLabel="Emoji" bare>
            <EmojiGrid items={rows} value={armedEmoji} onPick={pickEmoji} />
          </TraySection>
        ) : (
          <TrayEmpty>No emoji matches &ldquo;{query}&rdquo;.</TrayEmpty>
        );
      })()
    : EMOJI_SECTIONS.map((sec) => (
        <TraySection key={sec.key} label={sec.name} bare>
          <EmojiGrid items={sec.items} value={armedEmoji} onPick={pickEmoji} />
        </TraySection>
      ));

  const widgetGrid = (presets: WidgetPreset[], drawn: boolean) => {
    const pick = (k: string) => {
      const preset = presets.find((p) => p.id === k);
      if (preset) onSelect(widgetPlacementItem(preset));
    };
    const drag = (k: string, e: React.DragEvent<HTMLElement>) => {
      const preset = presets.find((p) => p.id === k);
      if (preset) startWidgetDrag(preset, e);
    };
    return drawn ? (
      <StickerGrid
        items={presets.map((preset) => ({
          key: preset.id,
          name: preset.label,
          title: preset.meaning
            ? `${preset.label}: ${preset.meaning}`
            : undefined,
          // The art itself fills the cell; the placement size never
          // reaches the tray.
          art: preset.preview ? (
            <preset.preview />
          ) : (
            <WidgetPreview preset={preset} width={90} height={60} />
          ),
        }))}
        value={value}
        onChange={pick}
        onDragStart={(it, e) => drag(it.key, e)}
      />
    ) : (
      <PlateGrid
        items={presets.map((preset) => ({
          key: preset.id,
          name: preset.label,
          title: preset.meaning
            ? `${preset.label}: ${preset.meaning}`
            : undefined,
          wire: <WidgetPreview preset={preset} width={90} height={60} />,
        }))}
        value={value}
        onChange={pick}
        onDragStart={(it, e) => drag(it.key, e)}
      />
    );
  };

  const stickerSections: {
    key: string;
    name: string;
    presets: WidgetPreset[];
    drawn: boolean;
  }[] = [
    {
      key: "review",
      name: "Review",
      presets: REVIEW_STICKER_PRESETS.filter((p) =>
        matchesWidgetQuery(p, query),
      ),
      drawn: true,
    },
    ...PRODUCT_FAMILIES.map((f) => ({
      key: f.key,
      name: f.name,
      presets: WIDGET_PRESETS.filter(
        (p) => p.family === f.key && matchesWidgetQuery(p, query),
      ),
      drawn: false,
    })),
  ].filter((s) => s.presets.length);
  const stickersBody = stickerSections.length ? (
    stickerSections.map((s) => (
      <TraySection key={s.key} label={s.name} bare>
        {widgetGrid(s.presets, s.drawn)}
      </TraySection>
    ))
  ) : (
    <TrayEmpty>No sticker matches &ldquo;{query}&rdquo;.</TrayEmpty>
  );

  const icons = ICON_STICKER_PRESETS.filter((p) => hit(p.label));
  const iconsBody = icons.length ? (
    <TraySection ariaLabel="Icons" bare>
      <TileGrid
        items={icons.map((preset) => {
          const Preview = preset.preview!;
          return {
            key: preset.id,
            name: preset.label,
            node: (
              <span className="block size-7 [&>div]:h-full [&>div]:w-full">
                <Preview />
              </span>
            ),
          };
        })}
        value={value}
        onChange={(k) => {
          const preset = icons.find((p) => p.id === k);
          if (preset) onSelect(widgetPlacementItem(preset));
        }}
        columns={8}
        rung="lg"
      />
    </TraySection>
  ) : (
    <TrayEmpty>No icon matches &ldquo;{query}&rdquo;.</TrayEmpty>
  );

  const tabs: TrayTab[] = [
    {
      key: "emoji",
      name: "Emoji",
      node: tabGlyph(Smile),
      body: <>{emojiBody}</>,
    },
    {
      key: "stickers",
      name: "Stickers",
      node: tabGlyph(Sticker),
      body: <>{stickersBody}</>,
    },
    { key: "icons", name: "Icons", node: tabGlyph(Shapes), body: iconsBody },
  ];

  return (
    <div className="contents" onPointerDownCapture={keepEditorFocus}>
      <TabbedTray
        tabs={tabs}
        deckTop={
          <TraySearch
            value={query}
            onChange={setQuery}
            placeholder="Search emoji, stickers, icons…"
            label="Search emoji, stickers and icons"
          />
        }
      />
    </div>
  );
}
